import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { BookDetailModal } from '@/components/books/BookDetailModal';
import {
  BookmarkStatusControl,
  BOOKMARK_STATUS_LABELS,
} from '@/components/books/BookmarkStatusControl';
import { ReadingLogSection } from '@/components/reading-log/ReadingLogSection';
import { ReaderAvatar } from '@/components/ui/ReaderAvatar';
import { SectionTitle } from '@/components/ui/SectionTitle';
import { StarRating } from '@/components/ui/StarRating';
import { BookCover } from '@/components/books/BookCover';
import { bookmarksService } from '@/services/bookmarks.service';
import { reviewsService } from '@/services/reviews.service';
import { loggedBooksService } from '@/services/logged-books.service';
import { ApiError } from '@/services/http';
import { formatRelativeTime } from '@/lib/relative-time';
import { useAccountStore } from '@/store/account.store';
import { useReadersStore } from '@/store/readers.store';
import type {
  BookView,
  BookmarkStatus,
  BookmarkView,
  LoggedBookView,
  ReaderReviewView,
} from '@/types/api';

/**
 * The active shelves rendered on the Bookshelf, in display order. Each maps
 * to one `bookmarks.status` value. `completed` is intentionally NOT a shelf
 * here — finished catalog books are folded into the Reading Log section
 * (alongside off-catalog logged books) further down the page.
 */
const SHELVES: { key: BookmarkStatus; title: string; emptyHint: string }[] = [
  {
    key: 'in_progress',
    title: 'Books In Progress',
    emptyHint: 'Set a book to “In progress” to track what you’re reading now.',
  },
  {
    key: 'none',
    title: 'Saved Books',
    emptyHint: 'Books you save but haven’t started yet land here.',
  },
];

/**
 * ReadingList — `/list`. The current reader's bookmarked books.
 *
 * Behaviour:
 *   - Lists `GET /bookmarks?readerId=…` for the active reader.
 *   - Each card carries a trash overlay that hits
 *     `DELETE /bookmarks/:id` and patches the list optimistically on
 *     success. Failures re-add the row and toast the server message.
 *   - Clicking a card opens the shared `BookDetailModal`; the modal
 *     delegates its own bookmark toggle back to `bookmarksService`, so
 *     a removal from inside the modal reflects here on re-render.
 *   - No reader selected → friendly nudge to /readers. Empty list →
 *     nudge back to /library.
 */
export function ReadingListPage() {
  const selectedReader = useReadersStore((s) => s.selectedReader());
  const [bookmarks, setBookmarks] = useState<BookmarkView[]>([]);
  // Ad-hoc Reading Log entries (books not in our catalog). Fetched once here
  // and shared with both the Reading Log section and the Reviews section.
  const [loggedBooks, setLoggedBooks] = useState<LoggedBookView[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedBook, setSelectedBook] = useState<BookView | null>(null);
  const [openTrackerOnSelect, setOpenTrackerOnSelect] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<Set<string>>(() => new Set());
  // Bookmark ids with an in-flight status change — disables that card's
  // status control until the PATCH resolves.
  const [pendingStatus, setPendingStatus] = useState<Set<string>>(() => new Set());

  const readerId = selectedReader?.id ?? null;

  useEffect(() => {
    if (!readerId) {
      setBookmarks([]);
      return;
    }
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const rows = await bookmarksService.list(readerId);
        if (!cancelled) setBookmarks(rows);
      } catch (err) {
        if (cancelled) return;
        toast.error(
          err instanceof ApiError ? err.message : "Could not load this reader's Bookshelf.",
        );
        setBookmarks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [readerId]);

  // Reading Log loads independently of bookmarks — it's non-critical chrome,
  // so a failure just leaves the section empty rather than blocking the page.
  useEffect(() => {
    if (!readerId) {
      setLoggedBooks([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await loggedBooksService.list(readerId);
        if (!cancelled) setLoggedBooks(rows);
      } catch (err) {
        if (cancelled) return;
        console.warn('Reading Log load failed:', err);
        setLoggedBooks([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [readerId]);

  const bookmarksByBookId = useMemo(() => {
    const map = new Map<string, BookmarkView>();
    for (const bm of bookmarks) map.set(bm.bookId, bm);
    return map;
  }, [bookmarks]);

  // Partition the bookmarks into one bucket per shelf, keyed by status. A
  // bookmark lands on exactly one shelf; unknown/legacy statuses fall back to
  // "Saved" so nothing silently disappears.
  const bookmarksByShelf = useMemo(() => {
    const groups: Record<BookmarkStatus, BookmarkView[]> = {
      none: [],
      in_progress: [],
      completed: [],
    };
    for (const bm of bookmarks) {
      (groups[bm.status] ?? groups.none).push(bm);
    }
    return groups;
  }, [bookmarks]);

  const handleRemove = useCallback(
    async (book: BookView) => {
      const bookmark = bookmarksByBookId.get(book.id);
      if (!bookmark) return;
      if (pendingRemove.has(bookmark.id)) return;

      setPendingRemove((prev) => {
        const next = new Set(prev);
        next.add(bookmark.id);
        return next;
      });

      // Optimistic removal — the list re-reads from state on re-render,
      // so we stash the current row for rollback in the catch.
      const previous = bookmarks;
      setBookmarks((prev) => prev.filter((bm) => bm.id !== bookmark.id));

      try {
        await bookmarksService.remove(bookmark.id);
        toast.success(`Removed “${book.title}”.`);
      } catch (err) {
        setBookmarks(previous);
        toast.error(err instanceof ApiError ? err.message : 'Could not remove that bookmark.');
      } finally {
        setPendingRemove((prev) => {
          const next = new Set(prev);
          next.delete(bookmark.id);
          return next;
        });
      }
    },
    [bookmarks, bookmarksByBookId, pendingRemove],
  );

  const handleStatusChange = useCallback(
    async (book: BookView, next: BookmarkStatus) => {
      const bookmark = bookmarksByBookId.get(book.id);
      if (!bookmark || bookmark.status === next) return;
      if (pendingStatus.has(bookmark.id)) return;

      setPendingStatus((prev) => new Set(prev).add(bookmark.id));

      // Optimistic: flip the status locally so the card hops to the new
      // shelf immediately. Stash the prior list for rollback on failure.
      const previous = bookmarks;
      setBookmarks((prev) =>
        prev.map((bm) => (bm.id === bookmark.id ? { ...bm, status: next } : bm)),
      );

      try {
        await bookmarksService.updateStatus(bookmark.id, next);
        toast.success(`Moved “${book.title}” to ${BOOKMARK_STATUS_LABELS[next]}.`);
      } catch (err) {
        setBookmarks(previous);
        toast.error(err instanceof ApiError ? err.message : 'Could not update the status.');
      } finally {
        setPendingStatus((prev) => {
          const updated = new Set(prev);
          updated.delete(bookmark.id);
          return updated;
        });
      }
    },
    [bookmarks, bookmarksByBookId, pendingStatus],
  );

  // One tile renderer shared by the active shelves AND the Reading Log's
  // completed-catalog-books grid, so a finished book looks and behaves the
  // same wherever it appears (cover/title open the modal; status control +
  // remove stay live).
  const renderBookmarkTile = useCallback(
    (bm: BookmarkView) => (
      <ShelfBookCard
        key={bm.id}
        bookmark={bm}
        removePending={pendingRemove.has(bm.id)}
        statusPending={pendingStatus.has(bm.id)}
        onOpen={() => {
          setOpenTrackerOnSelect(false);
          setSelectedBook(bm.book);
        }}
        onStatusChange={(next) => void handleStatusChange(bm.book, next)}
        onRemove={() => void handleRemove(bm.book)}
      />
    ),
    [pendingRemove, pendingStatus, handleStatusChange, handleRemove],
  );

  if (!selectedReader) {
    return (
      <AppShell mode="kid">
        <NoReaderState />
      </AppShell>
    );
  }

  return (
    <AppShell mode="kid">
      <div className="flex flex-col gap-6">
        <SectionTitle
          kicker=""
          heading={
            <span className="inline-flex items-center gap-3">
              <ReaderAvatar
                name={selectedReader.name}
                avatarSlug={selectedReader.avatarSlug}
                size="md"
              />
              <span>{selectedReader.name}&rsquo;s Bookshelf</span>
            </span>
          }
        />

        {loading ? (
          <ShelfTilesSkeleton />
        ) : bookmarks.length === 0 && loggedBooks.length === 0 ? (
          <EmptyReadingList readerName={selectedReader.name} />
        ) : (
          <div className="flex flex-col gap-8">
            {SHELVES.map(({ key, title, emptyHint }) => {
              const shelfBookmarks = bookmarksByShelf[key];
              return (
                <section key={key}>
                  <div className="mb-3 flex items-center gap-2">
                    <h2 className="font-display text-[16px] font-bold text-or-slate-900">{title}</h2>
                    <span className="text-[12px] font-semibold text-or-slate-400">
                      {shelfBookmarks.length}
                    </span>
                  </div>
                  {shelfBookmarks.length === 0 ? (
                    <p className="rounded-2xl border border-dashed border-or-slate-200 bg-white/80 px-5 py-6 text-center text-[13px] text-or-slate-500">
                      {emptyHint}
                    </p>
                  ) : (
                    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {shelfBookmarks.map((bm) => renderBookmarkTile(bm))}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>
        )}

        {/* Reading Log — every finished book in one banded section: catalog
            books marked completed (rendered with the shared tile so they keep
            their status control + modal) plus off-catalog logged books.
            Renders regardless of the shelves above. */}
        <ReadingLogSection
          readerId={selectedReader.id}
          readerName={selectedReader.name}
          books={loggedBooks}
          onChange={setLoggedBooks}
          completedBookmarks={bookmarksByShelf.completed}
          renderBookmarkTile={renderBookmarkTile}
        />

        {/* Reviews & ratings this reader has left — catalog reviews plus the
            private ratings/notes from Reading Log entries, merged + sorted. */}
        <ReaderReviewsSection
          readerId={selectedReader.id}
          readerName={selectedReader.name}
          loggedBooks={loggedBooks}
        />
      </div>

      <BookDetailModal
        book={selectedBook}
        initialTrackerOpen={openTrackerOnSelect}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedBook(null);
            // Reset so the next non-badge open lands on Details rather than
            // re-entering the tracker for an unrelated book.
            setOpenTrackerOnSelect(false);
          }
        }}
        onSelectBook={(next) => {
          // Similar-book swap inside the modal — always land on Details,
          // never on the previous book's tracker.
          setOpenTrackerOnSelect(false);
          setSelectedBook(next);
        }}
      />
    </AppShell>
  );
}

/**
 * ShelfBookCard — a bookmarked catalog book rendered as the same horizontal
 * tile the Reading Log uses (cover thumbnail + title/author), laid out in a
 * grid. The cover and title open the BookDetailModal; the status dropdown
 * moves the book between shelves and the trash button removes it.
 */
/** Formats a completion timestamp into a "Finished {date}" label, or null. */
function formatFinishedDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function ShelfBookCard({
  bookmark,
  removePending,
  statusPending,
  onOpen,
  onStatusChange,
  onRemove,
}: {
  bookmark: BookmarkView;
  removePending: boolean;
  statusPending: boolean;
  onOpen: () => void;
  onStatusChange: (next: BookmarkStatus) => void;
  onRemove: () => void;
}) {
  const { book } = bookmark;
  const finishedLabel = formatFinishedDate(bookmark.completedAt);
  return (
    <li className="group flex gap-3 rounded-2xl border border-or-slate-100 bg-white p-4 shadow-sm transition hover:border-or-orange-200 hover:shadow-or-card">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Open ${book.title}`}
        className="shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-300/60"
      >
        <BookCover
          title={book.title}
          author={book.author}
          src={book.bookCover}
          seed={book.id}
          w={56}
          h={80}
          className="h-20 w-14 rounded-lg"
        />
      </button>
      <div className="flex min-w-0 flex-1 flex-col">
        <button type="button" onClick={onOpen} className="min-w-0 text-left">
          <div className="truncate font-display text-[14px] font-bold text-or-slate-900">
            {book.title}
          </div>
          {book.author ? (
            <div className="truncate text-[12px] text-or-slate-500">{book.author}</div>
          ) : null}
        </button>
        {finishedLabel ? (
          <div className="mt-1 text-[12px] text-or-slate-500">Finished {finishedLabel}</div>
        ) : null}
        <div className="mt-2 flex items-center gap-2">
          <BookmarkStatusControl
            status={bookmark.status}
            disabled={statusPending}
            onChange={onStatusChange}
          />
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
            disabled={removePending}
            aria-label={`Remove ${book.title} from your list`}
            className="ml-auto inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-or-rose-400 transition hover:bg-or-rose-100 hover:text-or-rose-600 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-rose-300/60"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
    </li>
  );
}

/** Skeleton for the shelf grids while bookmarks load. */
function ShelfTilesSkeleton() {
  return (
    <div className="flex flex-col gap-8">
      {[0, 1].map((s) => (
        <section key={s}>
          <div className="mb-3 h-4 w-32 animate-pulse rounded-full bg-or-slate-100" />
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[0, 1].map((i) => (
              <li
                key={i}
                className="flex animate-pulse gap-3 rounded-2xl border border-or-slate-100 bg-white p-4"
              >
                <div className="h-20 w-14 shrink-0 rounded-lg bg-or-slate-100" />
                <div className="flex-1 space-y-2 py-1">
                  <div className="h-3 w-2/3 rounded-full bg-or-slate-100" />
                  <div className="h-3 w-1/3 rounded-full bg-or-slate-100" />
                  <div className="mt-3 h-7 w-28 rounded-full bg-or-slate-100" />
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function EmptyReadingList({ readerName }: { readerName: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-or-slate-200 bg-white/80 px-8 py-12 text-center">
      <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-or-orange-50 text-or-orange-600">
        <BookOpen className="h-5 w-5" aria-hidden />
      </div>
      <p className="mt-3 font-display text-[18px] font-bold text-or-slate-900">
        {readerName}&rsquo;s Bookshelf is empty
      </p>
      <p className="mt-1 text-[13px] text-or-slate-600">
        Tap the bookmark icon on any book to add it to their Bookshelf.
      </p>
      <Link
        to="/library"
        className="or-gradient-btn mt-5 inline-flex h-10 items-center gap-1.5 rounded-full px-5 text-[13px] font-semibold"
      >
        Browse the Library →
      </Link>
    </div>
  );
}

function NoReaderState() {
  return (
    <div className="rounded-2xl border border-dashed border-or-slate-200 bg-white/80 px-8 py-12 text-center">
      <p className="font-display text-[18px] font-bold text-or-slate-900">Pick a reader first</p>
      <p className="mt-1 text-[13px] text-or-slate-600">
        Every Bookshelf is per-reader — choose one from the header, or add a new one.
      </p>
      <Link
        to="/readers"
        className="or-gradient-btn mt-5 inline-flex h-10 items-center gap-1.5 rounded-full px-5 text-[13px] font-semibold"
      >
        Manage readers →
      </Link>
    </div>
  );
}

/**
 * ReaderReviewsSection — the reader's own ratings + reviews, shown at the
 * bottom of the Bookshelf. Fetches catalog reviews (keyed by `readerId`) and
 * merges in the private ratings/notes from Reading Log entries (passed down
 * so we don't double-fetch), date-sorted into one 1-col (mobile) / 2-col
 * (sm+) grid. The owner sees their own pending/rejected comments (with a
 * status badge) — these are their own rows on their own shelf.
 */
function ReaderReviewsSection({
  readerId,
  readerName,
  loggedBooks,
}: {
  readerId: string;
  readerName: string;
  loggedBooks: LoggedBookView[];
}) {
  const [reviews, setReviews] = useState<ReaderReviewView[] | null>(null);
  // Mirrors the parental-control toggle (and BookReviewsPanel): when the
  // family has comments off, ratings still show but comment text is hidden.
  // Logged-book notes are private (never moderated, never shared) so they
  // ignore this gate.
  const commentsVisible = useAccountStore((s) => s.account?.commentsEnabled !== false);

  useEffect(() => {
    let cancelled = false;
    setReviews(null);

    (async () => {
      try {
        const rows = await reviewsService.listForReader(readerId);
        if (!cancelled) setReviews(rows);
      } catch (err) {
        if (cancelled) return;
        // Non-critical — the Bookshelf works fine without this section.
        console.warn('Reader reviews load failed:', err);
        setReviews([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [readerId]);

  // A logged book surfaces here only once it has a rating or a note; a bare
  // title-only entry stays on the Reading Log shelf and out of reviews.
  const loggedWithReview = useMemo(
    () => loggedBooks.filter((b) => b.rating !== null || (b.note?.trim() ?? '') !== ''),
    [loggedBooks],
  );

  // Merge catalog reviews + logged-book reviews, newest first.
  const rows = useMemo(() => {
    const catalog = (reviews ?? []).map((review) => ({
      key: `review-${review.id}`,
      createdAt: review.createdAt,
      node: <ReaderReviewCard key={`review-${review.id}`} review={review} commentsVisible={commentsVisible} />,
    }));
    const logged = loggedWithReview.map((book) => ({
      key: `logged-${book.id}`,
      createdAt: book.createdAt,
      node: <LoggedReviewCard key={`logged-${book.id}`} book={book} />,
    }));
    return [...catalog, ...logged].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [reviews, loggedWithReview, commentsVisible]);

  return (
    <section className="mt-2">
      <div className="or-kicker mb-1.5">Reviews &amp; ratings</div>
      <h2 className="font-display text-[18px] font-bold text-or-slate-900">
        {readerName}&rsquo;s reviews &amp; ratings
      </h2>

      <div className="mt-4">
        {reviews === null ? (
          <ReviewCardsSkeleton />
        ) : rows.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-or-slate-200 bg-white/80 px-5 py-6 text-center text-[13px] text-or-slate-500">
            No reviews yet — rate or review a book and it&rsquo;ll show up here.
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">{rows.map((row) => row.node)}</ul>
        )}
      </div>
    </section>
  );
}

function ReaderReviewCard({
  review,
  commentsVisible,
}: {
  review: ReaderReviewView;
  commentsVisible: boolean;
}) {
  // Comment text + its moderation badge only surface when the family has
  // comments enabled; ratings are always shown.
  const showComment = commentsVisible && review.commentText !== null;
  const pending = commentsVisible && review.commentStatus === 'pending';
  const rejected = commentsVisible && review.commentStatus === 'rejected';
  return (
    <li className="flex gap-3 rounded-2xl border border-or-slate-100 bg-white p-4 shadow-sm">
      {review.bookCover ? (
        <img
          src={review.bookCover}
          alt=""
          loading="lazy"
          className="h-20 w-14 shrink-0 rounded-lg object-cover"
        />
      ) : (
        <div className="flex h-20 w-14 shrink-0 items-center justify-center rounded-lg bg-or-slate-100 text-or-slate-400">
          <BookOpen className="h-5 w-5" aria-hidden />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate font-display text-[14px] font-bold text-or-slate-900">
          {review.bookTitle}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          <StarRating value={review.rating} readOnly size="sm" />
          <span className="text-[12px] text-or-slate-500">
            {formatRelativeTime(review.createdAt)}
          </span>
          {pending ? (
            <span className="rounded-full bg-or-orange-100 px-2 py-0.5 text-[11px] font-semibold text-or-orange-700">
              Awaiting approval
            </span>
          ) : rejected ? (
            <span className="rounded-full bg-or-rose-100 px-2 py-0.5 text-[11px] font-semibold text-or-rose-700">
              Not approved
            </span>
          ) : null}
        </div>
        {showComment ? (
          <p className="mt-1.5 line-clamp-3 whitespace-pre-wrap text-[13px] leading-relaxed text-or-slate-700">
            {review.commentText}
          </p>
        ) : (
          <p className="mt-1.5 text-[12px] italic text-or-slate-500">Star rating only.</p>
        )}
      </div>
    </li>
  );
}

/**
 * LoggedReviewCard — the reviews-section card for a Reading Log entry. Mirrors
 * `ReaderReviewCard` but for off-catalog books: rating is optional (shown
 * only when set), there's no moderation badge, and the note is always visible
 * (it's private — never moderated, never gated by the comments toggle). A
 * "Reading Log" tag marks it as a self-logged book rather than a catalog one.
 */
function LoggedReviewCard({ book }: { book: LoggedBookView }) {
  return (
    <li className="flex gap-3 rounded-2xl border border-or-slate-100 bg-white p-4 shadow-sm">
      <BookCover
        title={book.title}
        author={book.author}
        src={book.coverUrl}
        seed={book.id}
        w={56}
        h={80}
        className="h-20 w-14 shrink-0 rounded-lg"
      />
      <div className="min-w-0 flex-1">
        <div className="truncate font-display text-[14px] font-bold text-or-slate-900">
          {book.title}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          {book.rating ? <StarRating value={book.rating} readOnly size="sm" /> : null}
          <span className="text-[12px] text-or-slate-500">
            {formatRelativeTime(book.createdAt)}
          </span>
          <span className="rounded-full bg-or-slate-100 px-2 py-0.5 text-[11px] font-semibold text-or-slate-500">
            Reading Log
          </span>
        </div>
        {book.note ? (
          <p className="mt-1.5 line-clamp-3 whitespace-pre-wrap text-[13px] leading-relaxed text-or-slate-700">
            {book.note}
          </p>
        ) : (
          <p className="mt-1.5 text-[12px] italic text-or-slate-500">Star rating only.</p>
        )}
      </div>
    </li>
  );
}

function ReviewCardsSkeleton() {
  return (
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {[0, 1].map((i) => (
        <li
          key={i}
          className="flex animate-pulse gap-3 rounded-2xl border border-or-slate-100 bg-white p-4"
        >
          <div className="h-20 w-14 shrink-0 rounded-lg bg-or-slate-100" />
          <div className="flex-1 space-y-2 py-1">
            <div className="h-3 w-2/3 rounded-full bg-or-slate-100" />
            <div className="h-3 w-1/3 rounded-full bg-or-slate-100" />
            <div className="h-3 w-full rounded-full bg-or-slate-100" />
          </div>
        </li>
      ))}
    </ul>
  );
}
